import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { Package, Check, List, Plus, Edit, Trash2 } from "lucide-react";
import { Product, ProductCategory } from "@/types/product";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useProducts } from "./ProductsContext";
import { CreateRuleModal } from "./CreateRuleModal";
import { CreateCategoryModal } from "./CreateCategoryModal";
import { useAuth } from "@/hooks/useAuth";

interface CreateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: Product) => void;
  categories: ProductCategory[];
  editingProduct?: Product | null;
}

export function CreateProductModal({
  isOpen,
  onClose,
  onSave,
  categories,
  editingProduct,
}: CreateProductModalProps) {
  const { toast } = useToast();
  const { createRule, updateRule, deleteRule, products, createCategory, slug } =
    useProducts();
  const { company } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    sku: "",
    categoryId: "",
    unitPrice: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableCategories, setAvailableCategories] = useState<
    ProductCategory[]
  >([]);

  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<any>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  const currentProduct = editingProduct
    ? products.find((p) => p.id === editingProduct.id) || editingProduct
    : null;

  const getCurrencySymbol = () => {
    if ((company as any)?.currency) return (company as any).currency;

    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {
      console.error("Error reading currency from storage", e);
    }
    return "$";
  };

  const currencySymbol = getCurrencySymbol();

  useEffect(() => {
    if (editingProduct && isOpen) {
      setFormData({
        name: editingProduct.name || "",
        description: editingProduct.description || "",
        sku: editingProduct.sku || "",
        categoryId: editingProduct.categoryId || "",
        unitPrice: (editingProduct.unitPrice || 0).toString(),
      });
    } else if (isOpen) {
      resetForm();
    }
  }, [editingProduct, isOpen]);

  useEffect(() => {
    const filteredCategories = categories.filter((cat) => {
      const isValid = cat.id && cat.id.trim() !== "" && cat.id !== "";
      return isValid;
    });
    setAvailableCategories(filteredCategories);
  }, [categories]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      sku: "",
      categoryId: "",
      unitPrice: "",
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (
    field: keyof typeof formData,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre del producto es obligatorio";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "El nombre debe tener al menos 2 caracteres";
    }

    if (!formData.description.trim()) {
      newErrors.description = "La descripción es obligatoria";
    } else if (formData.description.trim().length < 10) {
      newErrors.description =
        "La descripción debe tener al menos 10 caracteres";
    }

    if (formData.sku.trim().length > 0 && formData.sku.trim().length < 3) {
      newErrors.sku = "El código debe tener al menos 3 caracteres";
    }

    if (!formData.unitPrice.trim()) {
      newErrors.unitPrice = "El precio unitario es obligatorio";
    } else {
      const unitPrice = parseFloat(formData.unitPrice);
      if (isNaN(unitPrice)) {
        newErrors.unitPrice = "El precio debe ser un número válido";
      } else if (unitPrice <= 0) {
        newErrors.unitPrice = "El precio debe ser mayor a 0";
      } else if (unitPrice > 1000000) {
        newErrors.unitPrice = "El precio no puede ser mayor a $1,000,000";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast({
        title: "Error de validación",
        description: "Por favor, corrige los errores en el formulario",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleCreateRule = async (ruleData: any) => {
    if (!currentProduct) return;

    if (ruleToEdit) {
      await updateRule(ruleToEdit.id, ruleData);
    } else {
      await createRule({
        ...ruleData,
        product_id: currentProduct.id,
      });
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!currentProduct) return;
    if (window.confirm("¿Estás seguro de que deseas eliminar esta regla?")) {
      await deleteRule(ruleId, currentProduct.id);
    }
  };

  const formatPriceModifier = (modifier: string | number, type: string) => {
    const numModifier =
      typeof modifier === "string" ? parseFloat(modifier) : modifier;
    const sign = numModifier >= 0 ? "+" : "";
    if (type === "percentage") {
      return `${sign}${numModifier}%`;
    }
    return `${sign}$${numModifier.toFixed(2)}`;
  };

  const getPriceTypeLabel = (type: string) => {
    switch (type) {
      case "percentage":
        return "Porcentaje";
      case "fixed":
        return "Fijo";
      default:
        return type;
    }
  };

  const handleSaveCategory = async (categoryData: any) => {
    const newCat = await createCategory(categoryData);
    if (newCat) {
      setFormData((prev) => ({ ...prev, categoryId: newCat.id }));
      setIsCategoryModalOpen(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const productData: Product = {
      id: editingProduct?.id || Date.now().toString(),
      name: formData.name.trim(),
      description: formData.description.trim(),
      sku: formData.sku.trim(),
      categoryId: formData.categoryId || "",
      unitPrice: parseFloat(formData.unitPrice),
      unit: "Unidad",
      isActive: true,
      attachments: [],
      rules: currentProduct?.rules || [],
      createdAt: editingProduct?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    onSave(productData);
    handleClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-hidden p-0 custom-scrollbar rounded-[24px] border-slate-100 flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col h-full overflow-hidden">
          
          {/* Header */}
          <div className="bg-white border-b border-slate-100 p-4 sm:px-6 sm:py-4 z-10 relative flex-shrink-0">
            <DialogHeader>
              <div className="flex items-center space-x-3">
                <div className="bg-indigo-100/80 p-2.5 rounded-xl">
                  <Package className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="text-left">
                  <DialogTitle className="text-lg font-bold text-slate-900 tracking-tight">
                    {editingProduct ? "Editar Producto" : "Nuevo Producto"}
                  </DialogTitle>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {editingProduct ? "Modifica los datos del producto o servicio" : "Registra un nuevo producto o servicio en el catálogo"}
                  </p>
                </div>
              </div>
            </DialogHeader>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50/50">
            <div className="space-y-6">
              
              <div className="flex items-center space-x-2 mb-4">
                <Package className="h-5 w-5 text-indigo-500" />
                <h3 className="text-base font-bold text-slate-800 tracking-tight">
                  Información del Producto
                </h3>
              </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Ej: Licencia de Windows 11 Pro"
                className={`mt-1 h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                  errors.name ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50" : ""
                }`}
                maxLength={100}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                {(formData.name || "").length}/100 caracteres
              </p>
            </div>

            <div>
              <Label htmlFor="sku" className="text-sm font-medium text-slate-700">Código (SKU)</Label>
              <Input
                id="sku"
                value={formData.sku}
                onChange={(e) => handleChange("sku", e.target.value)}
                placeholder="Ej: WIN11-PRO-LIC"
                className={`mt-1 h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                  errors.sku ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50" : ""
                }`}
                maxLength={50}
              />
              {errors.sku && (
                <p className="text-red-500 text-sm mt-1">{errors.sku}</p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                {(formData.sku || "").length}/50 caracteres
              </p>
            </div>

            <div>
              <Label htmlFor="unitPrice" className="text-sm font-medium text-slate-700">Precio unitario *</Label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span className="text-gray-500 font-medium">
                    {currencySymbol}
                  </span>
                </div>
                <Input
                  id="unitPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  max="1000000"
                  value={formData.unitPrice}
                  onChange={(e) => handleChange("unitPrice", e.target.value)}
                  placeholder="0.00"
                  className={`pl-9 h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                    errors.unitPrice ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50" : ""
                  }`}
                />
              </div>
              {errors.unitPrice && (
                <p className="text-red-500 text-sm mt-1">{errors.unitPrice}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <Label
                htmlFor="category"
                className="flex items-center justify-between text-sm font-medium text-slate-700"
              >
                Categoría
              </Label>
              <div className="flex gap-2 mt-1">
                <Select
                  value={formData.categoryId}
                  onValueChange={(value) => {
                    handleChange("categoryId", value);
                  }}
                >
                  <SelectTrigger className="flex-1 h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus:ring-indigo-500 transition-all shadow-sm">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-category">Sin categoría</SelectItem>

                    {availableCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: cat.color || "#6b7280" }}
                          />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 border-slate-200 text-slate-500 hover:text-indigo-600 hover:bg-slate-50 transition-colors rounded-lg"
                  onClick={() => setIsCategoryModalOpen(true)}
                  title="Crear nueva categoría"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {errors.categoryId && (
                <p className="text-red-500 text-sm mt-1">{errors.categoryId}</p>
              )}
            </div>

            {editingProduct && currentProduct && (
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label>Ajustes de Precio</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRuleToEdit(null);
                      setIsRuleModalOpen(true);
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Agregar Ajuste
                  </Button>
                </div>

                <div className="border rounded-md p-3 max-h-60 overflow-y-auto space-y-2 bg-white">
                  {currentProduct.rules && currentProduct.rules.length > 0 ? (
                    currentProduct.rules.map((rule) => (
                      <div
                        key={rule.id}
                        className="flex items-center justify-between p-2 rounded-md border bg-slate-50"
                      >
                        <div>
                          <p className="text-sm font-medium flex items-center gap-2">
                            {rule.rule_name}
                            {rule.is_required && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] h-5 px-1.5"
                              >
                                Obligatorio
                              </Badge>
                            )}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span
                              className={
                                (typeof rule.price_modifier === "string"
                                  ? parseFloat(rule.price_modifier)
                                  : rule.price_modifier) >= 0
                                  ? "text-green-600 font-medium"
                                  : "text-red-600 font-medium"
                              }
                            >
                              {formatPriceModifier(
                                rule.price_modifier,
                                rule.price_type,
                              )}
                            </span>
                            <span>•</span>
                            <span>{getPriceTypeLabel(rule.price_type)}</span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              setRuleToEdit(rule);
                              setIsRuleModalOpen(true);
                            }}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteRule(rule.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No hay ajustes configurados
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="md:col-span-2">
              <Label htmlFor="description" className="text-sm font-medium text-slate-700">Descripción *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Describe tu producto o servicio..."
                rows={4}
                className={`mt-1 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                  errors.description ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50" : ""
                }`}
                maxLength={500}
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.description}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                {(formData.description || "").length}/500 caracteres
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-slate-100 p-4 sm:px-6 sm:py-4 flex-shrink-0">
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleClose}
            className="w-full sm:w-auto rounded-xl border-slate-200 hover:bg-slate-50 text-slate-700 font-medium transition-all h-11 px-6"
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            className="w-full sm:w-auto bg-slate-900 hover:bg-black text-white rounded-xl shadow-lg shadow-slate-900/20 font-medium transition-all h-11 px-8"
          >
            {editingProduct ? "Actualizar" : "Crear"} Producto
          </Button>
        </div>
      </div>
    </form>
  </DialogContent>

      <CreateRuleModal
        isOpen={isRuleModalOpen}
        onClose={() => {
          setIsRuleModalOpen(false);
          setRuleToEdit(null);
        }}
        onSave={handleCreateRule}
        editingRule={ruleToEdit}
      />

      <CreateCategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onSave={handleSaveCategory}
        categories={categories}
        slug={slug}
      />
    </Dialog>
  );
}
